/**
 * @file styles.ts
 * @description Centralized style definitions for all components
 * Single source of truth for all styling in the application
 */

import { CSSProperties } from 'react';

// Note theme definitions
export interface NoteTheme {
  name: string;
  container: {
    background: string;
    border?: string;
    boxShadow: string;
  };
  editor: {
    background: string;
    color: string;
    selection?: string;
  };
  header: {
    background: string;
    borderColor: string;
  };
}

export const noteThemes: Record<string, NoteTheme> = {
  default: {
    name: 'Default',
    container: {
      background: 'linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)',
      boxShadow: '0 1px 3px rgba(0, 0, 0, 0.05)'
    },
    editor: {
      background: '#ffffff',
      color: '#1f2937',
      selection: 'rgba(59, 130, 246, 0.15)'
    },
    header: {
      background: 'linear-gradient(135deg, #ffffff 0%, #fafbfc 100%)',
      borderColor: 'rgba(226, 232, 240, 0.8)'
    }
  },
  legalPad: {
    name: 'Legal Pad',
    container: {
      background: 'linear-gradient(135deg, #fffbeb 0%, #fef3c7 100%)',
      border: '1px solid #f59e0b',
      boxShadow: '0 1px 3px rgba(245, 158, 11, 0.1)'
    },
    editor: {
      background: '#fffef5',
      color: '#451a03',
      selection: 'rgba(245, 158, 11, 0.2)'
    },
    header: {
      background: 'linear-gradient(135deg, #fffef5 0%, #fef3c7 100%)',
      borderColor: 'rgba(245, 158, 11, 0.3)'
    }
  },
  mint: {
    name: 'Mint Fresh',
    container: {
      background: 'linear-gradient(135deg, #ecfdf5 0%, #d1fae5 100%)',
      boxShadow: '0 1px 3px rgba(16, 185, 129, 0.08)'
    },
    editor: {
      background: '#f0fdf9',
      color: '#064e3b',
      selection: 'rgba(16, 185, 129, 0.15)'
    },
    header: {
      background: 'linear-gradient(135deg, #f0fdf9 0%, #d1fae5 100%)',
      borderColor: 'rgba(16, 185, 129, 0.25)'
    }
  },
  lavender: {
    name: 'Lavender',
    container: {
      background: 'linear-gradient(135deg, #f3f4f6 0%, #e9d5ff 100%)',
      boxShadow: '0 1px 3px rgba(147, 51, 234, 0.08)'
    },
    editor: {
      background: '#faf5ff',
      color: '#3b0764',
      selection: 'rgba(147, 51, 234, 0.15)'
    },
    header: {
      background: 'linear-gradient(135deg, #faf5ff 0%, #ede9fe 100%)',
      borderColor: 'rgba(147, 51, 234, 0.2)'
    }
  },
  warmCream: {
    name: 'Warm Cream',
    container: {
      background: 'linear-gradient(135deg, #fef6e4 0%, #f3d5b1 100%)',
      boxShadow: '0 1px 3px rgba(217, 119, 6, 0.08)'
    },
    editor: {
      background: '#fefcf8',
      color: '#3f3a36',
      selection: 'rgba(217, 119, 6, 0.15)'
    },
    header: {
      background: 'linear-gradient(135deg, #fefcf8 0%, #fed7aa 100%)',
      borderColor: 'rgba(217, 119, 6, 0.25)'
    }
  },
  skyBlue: {
    name: 'Sky Blue',
    container: {
      background: 'linear-gradient(135deg, #eff6ff 0%, #bfdbfe 100%)',
      boxShadow: '0 1px 3px rgba(59, 130, 246, 0.08)'
    },
    editor: {
      background: '#f0f9ff',
      color: '#1e3a8a',
      selection: 'rgba(59, 130, 246, 0.15)'
    },
    header: {
      background: 'linear-gradient(135deg, #f0f9ff 0%, #dbeafe 100%)',
      borderColor: 'rgba(59, 130, 246, 0.25)'
    }
  }
};

// Type definitions for style objects
export interface StyleDefinitions {
  // Helper method
  combine: (...styles: (CSSProperties | undefined)[]) => CSSProperties;

  // Size utilities
  size: {
    minHScreen: CSSProperties;
    wFull: CSSProperties;
    w8: CSSProperties;
    w64: CSSProperties;
  };

  // Animation styles
  animation: {
    fadeIn: CSSProperties;
  };

  // States styles
  states: {
    hover: {
      gray50: CSSProperties;
    };
  };

  // Modal styles
  modal: {
    overlay: CSSProperties;
    container: CSSProperties;
    containerCompact: CSSProperties;
    header: CSSProperties;
    title: CSSProperties;
    subtitle: CSSProperties;
    body: CSSProperties;
    description: CSSProperties;
    footer: CSSProperties;
    close: CSSProperties;
    closeHover: CSSProperties;
    icon: CSSProperties;
    tip: CSSProperties;
    tipStrong: CSSProperties;
  };

  // Form styles
  form: {
    group: CSSProperties;
    groupWithDropdown: CSSProperties;
    label: CSSProperties;
    input: CSSProperties;
    select: CSSProperties;
    textarea: CSSProperties;
    error: CSSProperties;
    helper: CSSProperties;
    tagContainer: CSSProperties;
    tagPill: CSSProperties;
    tagRemove: CSSProperties;
    tagRemoveButton: CSSProperties;
    tagInput: CSSProperties;
    tagInputWrapper: CSSProperties;
    tagAddButton: CSSProperties;
    tagSuggestionsDropdown: CSSProperties;
    tagSuggestionItem: CSSProperties;
    tagSuggestionItemHover: CSSProperties;
    slider: CSSProperties;
    checkbox: CSSProperties;
    checkboxLabel: CSSProperties;
    progressWrapper: CSSProperties;
    progressHeader: CSSProperties;
    progressLabel: CSSProperties;
    progressValue: CSSProperties;
    progressSlider: CSSProperties;
  };

  // Button styles
  button: {
    base: CSSProperties;
    primary: CSSProperties;
    primaryHoverEffect: CSSProperties;
    primaryGradientLarge: CSSProperties;
    secondary: CSSProperties;
    secondaryHoverEffect: CSSProperties;
    cancel: CSSProperties;
    danger: CSSProperties;
    small: CSSProperties;
    medium: CSSProperties;
    large: CSSProperties;
    gradient: CSSProperties;
    transparent: CSSProperties;
    text: CSSProperties;
    textDanger: CSSProperties;
    icon: CSSProperties;
    iconSmall: CSSProperties;
    iconSuccess: CSSProperties;
    iconDanger: CSSProperties;
    hero: CSSProperties;
  };

  // Card styles
  card: {
    elevated: CSSProperties;
  };

  // Display styles
  display: {
    block: CSSProperties;
    flex: CSSProperties;
    flexAlignCenter: CSSProperties;
  };

  // Background styles
  background: {
    white: CSSProperties;
    gray50: CSSProperties;
    gray100: CSSProperties;
  };

  // Layout styles
  layout: {
    container: CSSProperties;
    maxWidth: CSSProperties;
    flex: CSSProperties;
    flexAlignCenter: CSSProperties;
    flexCenterAll: CSSProperties;
    flexBetween: CSSProperties;
    flexColumn: CSSProperties;
    grid: CSSProperties;
    hidden: CSSProperties;
    relative: CSSProperties;
    absolute: CSSProperties;
    fixed: CSSProperties;
    headerButton: CSSProperties;
    headerButtonHover: CSSProperties;
    headerButtonSecondary: CSSProperties;
    headerButtonSecondaryHover: CSSProperties;
    headerButtonActive: CSSProperties;
  };

  // Spacing styles
  spacing: {
    paddingNone: CSSProperties;
    paddingXs: CSSProperties;
    paddingSm: CSSProperties;
    paddingMd: CSSProperties;
    paddingLg: CSSProperties;
    paddingXl: CSSProperties;
    padding2xl: CSSProperties;
    px2: CSSProperties;
    px3: CSSProperties;
    px4: CSSProperties;
    px6: CSSProperties;
    py2: CSSProperties;
    py3: CSSProperties;
    py4: CSSProperties;
    m0: CSSProperties;
    m1: CSSProperties;
    m2: CSSProperties;
    m3: CSSProperties;
    marginHorizontalSm: CSSProperties;
    marginHorizontalMd: CSSProperties;
    marginHorizontalLg: CSSProperties;
    marginHorizontalXl: CSSProperties;
    marginHorizontal2xl: CSSProperties;
    marginHorizontalAuto: CSSProperties;
    marginVerticalSm: CSSProperties;
    marginVerticalMd: CSSProperties;
    marginVerticalLg: CSSProperties;
    marginTopXs: CSSProperties;
    marginTopSm: CSSProperties;
    marginTopMd: CSSProperties;
    marginTopLg: CSSProperties;
    marginBottomXs: CSSProperties;
    marginBottomSm: CSSProperties;
    marginBottomMd: CSSProperties;
    marginBottomLg: CSSProperties;
    marginBottomXl: CSSProperties;
    marginLeftSm: CSSProperties;
    marginLeftMd: CSSProperties;
    marginLeftLg: CSSProperties;
    marginLeftXl: CSSProperties;
    marginRightSm: CSSProperties;
    marginRightMd: CSSProperties;
    marginRightLg: CSSProperties;
    gap1: CSSProperties;
    gap2: CSSProperties;
    gap3: CSSProperties;
    gap4: CSSProperties;
  };

  // Shadow styles
  shadows: {
    sm: CSSProperties;
    md: CSSProperties;
    lg: CSSProperties;
    xl: CSSProperties;
  };

  // Typography styles
  typography: {
    h1: CSSProperties;
    h2: CSSProperties;
    h3: CSSProperties;
    h4: CSSProperties;
    h5: CSSProperties;
    body: CSSProperties;
    bodySmall: CSSProperties;
    caption: CSSProperties;
    textXs: CSSProperties;
    textSm: CSSProperties;
    textBase: CSSProperties;
    textLg: CSSProperties;
    textXl: CSSProperties;
    text2xl: CSSProperties;
    text3xl: CSSProperties;
    fontNormal: CSSProperties;
    fontMedium: CSSProperties;
    fontSemibold: CSSProperties;
    fontBold: CSSProperties;
    textPrimary: CSSProperties;
    textSecondary: CSSProperties;
    textTertiary: CSSProperties;
    textWhite: CSSProperties;
    textCenter: CSSProperties;
    textLeft: CSSProperties;
    textRight: CSSProperties;
  };

  // Color styles
  colors: {
    primary: CSSProperties;
    primaryLight: CSSProperties;
    primaryDark: CSSProperties;
    secondary: CSSProperties;
    success: CSSProperties;
    warning: CSSProperties;
    error: CSSProperties;
    background: CSSProperties;
    surface: CSSProperties;
    surfaceLight: CSSProperties;
    border: CSSProperties;
    borderLight: CSSProperties;
    text: CSSProperties;
    textLight: CSSProperties;
    textDark: CSSProperties;
  };

  // Utility styles
  utility: {
    roundedSm: CSSProperties;
    roundedMd: CSSProperties;
    roundedLg: CSSProperties;
    roundedFull: CSSProperties;
    rounded: CSSProperties;
    shadow: CSSProperties;
    shadowMd: CSSProperties;
    shadowLg: CSSProperties;
    cursor: CSSProperties;
    pointerEventsNone: CSSProperties;
    overflowHidden: CSSProperties;
    overflowAuto: CSSProperties;
    border: CSSProperties;
    borderTop: CSSProperties;
    glass: CSSProperties;
    zIndex10: CSSProperties;
    zIndex50: CSSProperties;
    opacity90: CSSProperties;
  };

  // Position styles
  position: {
    relative: CSSProperties;
    absolute: CSSProperties;
    fixed: CSSProperties;
    sticky: CSSProperties;
    inset0: CSSProperties;
    top0: CSSProperties;
    right0: CSSProperties;
    bottom0: CSSProperties;
    left0: CSSProperties;
  };

  // Component-specific styles
  table: {
    container: CSSProperties;
    controlsWrapper: CSSProperties;
    searchResultsBar: CSSProperties;
    searchResultsText: CSSProperties;
    searchResultsHighlight: CSSProperties;
    spacerRow: CSSProperties;
    dragHandle: CSSProperties;
    dragHandleVisible: CSSProperties;
    dragOverlay: CSSProperties;
    dragOverlayCategory: CSSProperties;
    dragOverlaySubcategory: CSSProperties;
    dragOverlayItem: CSSProperties;
    mainContainer: CSSProperties;
    actionMenuWrapper: CSSProperties;
    actionCellWrapper: CSSProperties;
    cellActions: CSSProperties;
    actionButton: CSSProperties;
    actionButtonHover: CSSProperties;
    actionIcon: CSSProperties;
    actionIconHover: CSSProperties;
    menuContainer: CSSProperties;
    menuButton: CSSProperties;
    menuButtonHover: CSSProperties;
    menuButtonIcon: CSSProperties;
    colorPickerContainer: CSSProperties;
    colorPickerGrid: CSSProperties;
    colorButton: CSSProperties;
    colorButtonHover: CSSProperties;
    expanderCell: CSSProperties;
    contentCell: CSSProperties;
    contentHeaderCell: CSSProperties;
    levelOneBackground: CSSProperties;
    levelTwoBackground: CSSProperties;
    levelTwoContentPadding: CSSProperties;
    levelThreeContentPadding: CSSProperties;
    expandIconButton: CSSProperties;
    expandIcon: CSSProperties;
    categoryCard: CSSProperties;
    categoryCardContent: CSSProperties;
    categoryIcon: CSSProperties;
    categoryIconSvg: CSSProperties;
    categoryInfo: CSSProperties;
    categoryTitle: CSSProperties;
    subcategoryRow: CSSProperties;
    subcategoryText: CSSProperties;
    subcategoryIcon: CSSProperties;
    itemRow: CSSProperties;
    itemRowWithSubcategory: CSSProperties;
    itemRowWithoutSubcategory: CSSProperties;
    itemContent: CSSProperties;
    itemInfo: CSSProperties;
    itemLink: CSSProperties;
    tagPillContainer: CSSProperties;
    tagPill: CSSProperties;
    tagCounter: CSSProperties;
    notesContainer: CSSProperties;
    notesDescription: CSSProperties;
    commentButton: CSSProperties;
    progressContainer: CSSProperties;
    progressBarWrapper: CSSProperties;
    progressBarBackground: CSSProperties;
    progressText: CSSProperties;
    statusBadge: CSSProperties;
    statusBacklog: CSSProperties;
    statusInProgress: CSSProperties;
    statusDone: CSSProperties;
    statusBlocked: CSSProperties;
    headerCell: CSSProperties;
    tagHeaderCell: CSSProperties;
    notesHeaderCell: CSSProperties;
    linkText: CSSProperties;
    linkTextHover: CSSProperties;
    regularText: CSSProperties;
    categoryBadge: CSSProperties;
    emptyCell: CSSProperties;
  };

  // Notes styles
  notes: {
    header: CSSProperties;
    title: CSSProperties;
    newNoteButton: CSSProperties;
    sidebar: CSSProperties;
    searchBox: CSSProperties;
    searchInput: CSSProperties;
    notesList: CSSProperties;
    emptyState: CSSProperties;
    emptyStateIcon: CSSProperties;
    emptyStateText: CSSProperties;
    noteItem: CSSProperties;
    noteItemSelected: CSSProperties;
    noteItemTitle: CSSProperties;
    editor: CSSProperties;
    editorHeader: CSSProperties;
    editorTitle: CSSProperties;
    editorContent: CSSProperties;
    editorContentArea: CSSProperties;
    editorPlaceholder: CSSProperties;
    savingIndicator: CSSProperties;
    // Centralized styles for NotesView component
    headerFlexCenter: CSSProperties;
    headerFlexCenterGap2: CSSProperties;
    iconPrimary: CSSProperties;
    iconSecondary: CSSProperties;
    mainContainer: CSSProperties;
    searchIconPosition: CSSProperties;
    buttonWithMargin: CSSProperties;
    editorPlaceholderAbsolute: CSSProperties;
    emptyStateContainer: CSSProperties;
    emptyStateTitle: CSSProperties;
  };

  // Extended spacing
  extended: {
    p05: CSSProperties;
    pt4: CSSProperties;
    pl8: CSSProperties;
    px5: CSSProperties;
    py25: CSSProperties;
    gridCols2: CSSProperties;
    gradientBluePurple: CSSProperties;
    gradientGreen: CSSProperties;
    gradientBlue: CSSProperties;
    gradientYellow: CSSProperties;
  };

  // Modern component styles
  components: {
    settingsMenu: {
      dropdown: CSSProperties;
    };
    categoryModal: {
      overlay: CSSProperties;
      container: CSSProperties;
      content: CSSProperties;
      header: CSSProperties;
      title: CSSProperties;
      subtitle: CSSProperties;
      closeButton: CSSProperties;
      categoryCard: CSSProperties;
      categoryHeader: CSSProperties;
      categoryHeaderExpanded: CSSProperties;
      categoryName: CSSProperties;
      categoryActions: CSSProperties;
      expandButton: CSSProperties;
      actionButton: CSSProperties;
      actionButtonDanger: CSSProperties;
      subcategoryList: CSSProperties;
      subcategoryItem: CSSProperties;
      subcategoryText: CSSProperties;
      emptyState: CSSProperties;
      addButton: CSSProperties;
      addButtonPrimary: CSSProperties;
      inputField: CSSProperties;
      inputWrapper: CSSProperties;
    };
    localTimeDisplay: {
      container: CSSProperties;
      icon: CSSProperties;
      text: CSSProperties;
    };
    localDateDisplay: {
      container: CSSProperties;
      icon: CSSProperties;
      text: CSSProperties;
    };
    searchBar: {
      container: CSSProperties;
      input: CSSProperties;
      inputActive: CSSProperties;
      inputFocus: CSSProperties;
      icon: CSSProperties;
      results: CSSProperties;
      resultsText: CSSProperties;
      clearButton: CSSProperties;
    };
    viewToggle: {
      container: CSSProperties;
      buttonGroup: CSSProperties;
      button: CSSProperties;
      buttonActive: CSSProperties;
      buttonHover: CSSProperties;
      expandButton: CSSProperties;
      expandButtonHover: CSSProperties;
      collapseButton: CSSProperties;
      collapseButtonHover: CSSProperties;
      iconButton: CSSProperties;
      iconButtonHover: CSSProperties;
    };
    categoryCard: {
      container: CSSProperties;
      containerHover: CSSProperties;
      header: CSSProperties;
      leftSection: CSSProperties;
      expandButton: CSSProperties;
      avatar: CSSProperties;
      contentSection: CSSProperties;
      titleRow: CSSProperties;
      title: CSSProperties;
      completionBadge: CSSProperties;
      statsRow: CSSProperties;
      statItem: CSSProperties;
      statText: CSSProperties;
      progressBar: CSSProperties;
      progressFill: CSSProperties;
      actionsSection: CSSProperties;
      addButton: CSSProperties;
      menuButton: CSSProperties;
      dropdown: CSSProperties;
      dropdownItem: CSSProperties;
    };
    modernTable: {
      container: CSSProperties;
      table: CSSProperties;
      header: CSSProperties;
      headerCell: CSSProperties;
      row: CSSProperties;
      rowHover: CSSProperties;
      cell: CSSProperties;
      actionButton: CSSProperties;
      actionButtonHover: CSSProperties;
      dragHandleCell: CSSProperties;
      dragHandleHidden: CSSProperties;
      dragHandleVisible: CSSProperties;
      dragHandlePlaceholder: CSSProperties;
      dragHandleIcon: CSSProperties;
      dragHandleIconHover: CSSProperties;
    };
    inlineEdit: {
      container: CSSProperties;
      editContainer: CSSProperties;
      tagsContainer: CSSProperties;
      tagsEditWrapper: CSSProperties;
      tagPillEdit: CSSProperties;
      tagRemoveButton: CSSProperties;
      tagInput: CSSProperties;
      notesContainer: CSSProperties;
      notesTextarea: CSSProperties;
      statusContainer: CSSProperties;
      statusSelect: CSSProperties;
      placeholder: CSSProperties;
      suggestions: CSSProperties;
      suggestionItem: CSSProperties;
      helpText: CSSProperties;
    };
    notes: {
      modern: {
        container: CSSProperties;
        editor: CSSProperties;
        toolbar: CSSProperties;
        sidebar: CSSProperties;
        searchBox: CSSProperties;
        searchInput: CSSProperties;
        noteItem: CSSProperties;
        noteItemSelected: CSSProperties;
        actionButton: CSSProperties;
      };
      whiteboard: {
        container: CSSProperties;
        wrapper: CSSProperties;
        header: CSSProperties;
        headerLeft: CSSProperties;
        icon: CSSProperties;
        title: CSSProperties;
        searchContainer: CSSProperties;
        searchIcon: CSSProperties;
        searchInput: CSSProperties;
        clearButton: CSSProperties;
        saveIndicator: CSSProperties;
        editorContainer: CSSProperties;
        placeholder: CSSProperties;
        editor: CSSProperties;
        noResults: CSSProperties;
        themeSelector: {
          button: CSSProperties;
          dropdown: CSSProperties;
          dropdownItem: CSSProperties;
          dropdownItemActive: CSSProperties;
          colorDot: CSSProperties;
        };
      };
    };
  };
}

// Helper function to combine multiple styles
export const combineStyles = (...styles: (CSSProperties | undefined)[]): CSSProperties => {
  return Object.assign({}, ...styles.filter(Boolean));
};

// Style implementations
// @ts-ignore - inlineEdit styles are defined but not in interface yet
export const styleDefinitions: StyleDefinitions = {
  // Helper method
  combine: combineStyles,

  // Size utilities
  size: {
    minHScreen: { minHeight: '100vh' },
    wFull: { width: '100%' },
    w8: { width: '2rem' },
    w64: { width: '16rem' }
  },

  // Animation styles
  animation: {
    fadeIn: {}
  },

  // States styles
  states: {
    hover: {
      gray50: { backgroundColor: '#f9fafb' }
    }
  },

  // Modal styles
  modal: {
    overlay: {
      position: 'fixed' as const,
      inset: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.4)',
      backdropFilter: 'blur(8px)',
      display: 'flex',
      alignItems: 'flex-start',
      justifyContent: 'center',
      paddingTop: '100px',
      paddingBottom: '40px',
      overflowY: 'auto',
      zIndex: 1000,
    },
    container: {
      background: 'linear-gradient(145deg, rgba(255, 255, 255, 0.98) 0%, rgba(251, 252, 253, 0.98) 100%)',
      backdropFilter: 'blur(20px)',
      borderRadius: '1.25rem',
      border: '1px solid rgba(226, 232, 240, 0.8)',
      padding: '0',
      maxWidth: '28rem',
      width: '100%',
      maxHeight: 'calc(100vh - 140px)',
      display: 'flex',
      flexDirection: 'column',
      overflow: 'hidden',
      boxShadow: '0 20px 40px rgba(0, 0, 0, 0.12), 0 0 1px rgba(0, 0, 0, 0.05)',
    },
    containerCompact: {
      position: 'relative' as const,
      background: 'linear-gradient(145deg, rgba(255, 255, 255, 0.98) 0%, rgba(251, 252, 253, 0.98) 100%)',
      backdropFilter: 'blur(20px)',
      borderRadius: '1.25rem',
      border: '1px solid rgba(226, 232, 240, 0.8)',
      padding: '1.5rem',
      maxWidth: '30rem',
      width: '90%',
      maxHeight: '90vh',
      display: 'flex',
      flexDirection: 'column' as const,
      boxShadow: '0 20px 40px rgba(0, 0, 0, 0.12), 0 0 1px rgba(0, 0, 0, 0.05)',
    },
    header: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'flex-start',
      padding: '2rem 2rem 0 2rem',
      marginBottom: '1.5rem',
      gap: '1rem'
    },
    title: {
      fontSize: '1.125rem',
      fontWeight: '600',
      color: '#1f2937',
      lineHeight: '1.5'
    },
    subtitle: {
      fontSize: '0.875rem',
      color: '#6b7280',
      marginTop: '0.25rem'
    },
    body: {
      flex: '1 1 auto',
      overflowY: 'auto',
      overflowX: 'hidden',
      padding: '1rem 1.5rem',
      color: '#475569',
      fontSize: '0.875rem',
      lineHeight: '1.5',
      minHeight: '0',
      maxHeight: 'calc(100vh - 280px)'
    },
    description: {
      margin: '0',
      color: '#6b7280',
      fontSize: '0.95rem',
    },
    footer: {
      display: 'flex',
      justifyContent: 'flex-end',
      gap: '0.75rem',
      padding: '1rem 1.5rem 1.5rem 1.5rem',
      marginTop: 'auto',
      borderTop: '1px solid rgba(226, 232, 240, 0.5)',
      flexShrink: 0
    },
    close: {
      position: 'absolute' as const,
      top: '0.75rem',
      right: '0.75rem',
      width: '2.5rem',  // 40px
      height: '2.5rem', // 40px
      background: 'transparent',
      border: 'none',
      cursor: 'pointer',
      borderRadius: '0.5rem',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      color: '#6b7280',
      transition: 'all 0.2s'
    },
    closeHover: {
      background: 'rgba(243, 244, 246, 0.8)',
      color: '#374151',
    },
    icon: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      width: '2.5rem',
      height: '2.5rem',
      borderRadius: '0.625rem',
      background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
      marginRight: '1rem'
    },
    tip: {
      background: 'rgba(99, 102, 241, 0.08)',
      border: '1px solid rgba(99, 102, 241, 0.2)',
      borderRadius: '0.625rem',
      padding: '0.75rem 1rem',
      marginBottom: '1.5rem',
      fontSize: '0.8125rem',
      color: '#3b82f6',
      lineHeight: '1.4'
    },
    tipStrong: {
      fontWeight: '600',
      color: '#2563eb'
    }
  },

  // Form styles
  form: {
    group: {
      position: 'relative' as const,
      marginBottom: '1rem'
    },
    groupWithDropdown: {
      position: 'relative' as const,
      marginBottom: '1rem',
      paddingBottom: '12rem'
    },
    label: {
      display: 'block',
      fontSize: '0.875rem',
      fontWeight: '500',
      color: '#374151',
      marginBottom: '0.25rem'
    },
    input: {
      width: '100%',
      padding: '0.5rem 0.75rem',
      fontSize: '0.875rem',
      borderRadius: '0.375rem',
      border: '1px solid #d1d5db',
    },
    select: {
      width: '100%',
      padding: '0.5rem 0.75rem',
      fontSize: '0.875rem',
      borderRadius: '0.375rem',
      border: '1px solid #d1d5db',
      backgroundColor: 'white',
      cursor: 'pointer'
    },
    textarea: {
      width: '100%',
      padding: '0.5rem 0.75rem',
      fontSize: '0.875rem',
      borderRadius: '0.375rem',
      border: '1px solid #d1d5db',
      resize: 'vertical' as const,
      minHeight: '4rem'
    },
    error: {
      color: '#ef4444',
      fontSize: '0.875rem',
      marginTop: '0.25rem'
    },
    helper: {
      color: '#6b7280',
      fontSize: '0.75rem',
      marginTop: '0.25rem'
    },
    tagContainer: {
      position: 'relative' as const,
      display: 'flex',
      flexWrap: 'wrap' as const,
      gap: '0.5rem',
      marginBottom: '0.5rem',
      minHeight: '2.5rem'
    },
    tagPill: {
      display: 'inline-flex',
      alignItems: 'center',
      padding: '0.25rem 0.75rem',
      backgroundColor: '#eff6ff',
      color: '#1e40af',
      borderRadius: '9999px',
      fontSize: '0.875rem'
    },
    tagRemove: {
      marginLeft: '0.5rem',
      cursor: 'pointer',
      background: 'transparent',
      border: 'none',
      color: '#1e40af',
      fontSize: '1.25rem',
      lineHeight: '1'
    },
    tagRemoveButton: {
      marginLeft: '0.5rem',
      color: '#3b82f6',
      fontWeight: 'bold',
      background: 'none',
      border: 'none',
      cursor: 'pointer',
      padding: '0 0.25rem',
      fontSize: '1.125rem',
      lineHeight: 1
    },
    tagInput: {
      flex: '1',
      minWidth: '150px',
      padding: '0.25rem 0.5rem',
      fontSize: '0.875rem',
      border: 'none',
      outline: 'none',
      backgroundColor: 'transparent'
    },
    tagInputWrapper: {
      display: 'flex',
      gap: '0.5rem',
      marginBottom: '0.5rem'
    },
    tagAddButton: {
      padding: '0.5rem 1rem',
      background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
      color: 'white',
      borderRadius: '0.375rem',
      border: 'none',
      fontSize: '0.875rem',
      fontWeight: '500',
      cursor: 'pointer'
    },
    tagSuggestionsDropdown: {
      position: 'absolute' as const,
      top: '100%',
      left: 0,
      right: 0,
      marginTop: '0.25rem',
      backgroundColor: 'white',
      border: '1px solid #e5e7eb',
      borderRadius: '0.375rem',
      maxHeight: '12rem',
      overflowY: 'auto' as const,
      zIndex: 9999,
      boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)'
    },
    tagSuggestionItem: {
      display: 'block',
      width: '100%',
      padding: '0.5rem 0.75rem',
      textAlign: 'left' as const,
      fontSize: '0.875rem',
      color: '#374151',
      backgroundColor: 'white',
      border: 'none',
      cursor: 'pointer'
    },
    tagSuggestionItemHover: {
      backgroundColor: '#f3f4f6'
    },
    slider: {
      width: '100%',
      cursor: 'pointer'
    },
    checkbox: {
      width: '1rem',
      height: '1rem',
      cursor: 'pointer',
      accentColor: '#3b82f6'
    },
    checkboxLabel: {
      display: 'flex',
      alignItems: 'center',
      gap: '0.5rem',
      fontSize: '0.875rem',
      fontWeight: '500',
      color: '#374151',
      cursor: 'pointer'
    },
    progressWrapper: {
      marginTop: '0.75rem'
    },
    progressHeader: {
      display: 'flex',
      justifyContent: 'space-between',
      marginBottom: '0.25rem'
    },
    progressLabel: {
      fontSize: '0.875rem',
      fontWeight: '500',
      color: '#374151'
    },
    progressValue: {
      fontSize: '0.875rem',
      fontWeight: '600',
      color: '#3b82f6'
    },
    progressSlider: {
      width: '100%',
      accentColor: '#3b82f6',
      cursor: 'pointer'
    }
  },

  // Button styles
  button: {
    base: {
      padding: '0.625rem 1.25rem',
      borderRadius: '0.625rem',
      fontWeight: '500',
      fontSize: '0.875rem',
      cursor: 'pointer',
      border: '1px solid transparent',
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: '0.5rem',
      minWidth: '100px',
      lineHeight: '1.2'
    },
    primary: {
      background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
      color: 'white',
      border: '1px solid rgba(59, 130, 246, 0.2)',
      boxShadow: '0 2px 4px rgba(59, 130, 246, 0.15), 0 1px 2px rgba(59, 130, 246, 0.1)'
    },
    primaryHoverEffect: {
      boxShadow: '0 4px 8px rgba(59, 130, 246, 0.25), 0 2px 4px rgba(59, 130, 246, 0.15)',
      filter: 'brightness(1.05)'
    },
    secondary: {
      background: 'rgba(255, 255, 255, 0.9)',
      backdropFilter: 'blur(10px)',
      color: '#475569',
      border: '1px solid rgba(229, 231, 235, 0.8)',
      boxShadow: '0 1px 2px rgba(0, 0, 0, 0.05)'
    },
    secondaryHoverEffect: {
      background: 'rgba(248, 250, 252, 1)',
      borderColor: 'rgba(209, 213, 219, 1)',
      boxShadow: '0 2px 4px rgba(0, 0, 0, 0.08)'
    },
    small: {
      padding: '0.375rem 0.875rem',
      fontSize: '0.8125rem',
      minWidth: '80px'
    },
    medium: {
      padding: '0.5rem 1rem',
      fontSize: '0.875rem'
    },
    large: {
      padding: '0.75rem 1.5rem',
      fontSize: '1rem'
    },
    cancel: {
      padding: '0.75rem 1.5rem',
      color: '#6b7280',
      backgroundColor: 'rgba(243, 244, 246, 0.8)',
      backdropFilter: 'blur(10px)',
      borderRadius: '0.75rem',
      border: '1px solid rgba(229, 231, 235, 0.6)',
      fontSize: '0.875rem',
      fontWeight: '500',
      cursor: 'pointer',
      transition: 'all 0.2s ease',
      minWidth: '100px',
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center'
    },
    gradient: {
      padding: '0.75rem 1.5rem',
      background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
      color: 'white',
      borderRadius: '0.75rem',
      border: '1px solid rgba(59, 130, 246, 0.2)',
      fontSize: '0.875rem',
      fontWeight: '500',
      cursor: 'pointer',
      transition: 'all 0.2s ease',
      boxShadow: '0 2px 8px rgba(59, 130, 246, 0.25)',
      minWidth: '100px',
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center'
    },
    primaryGradientLarge: {
      background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
      color: 'white',
      border: 'none',
      borderRadius: '1rem',
      padding: '0.75rem 1.25rem',
      fontSize: '0.875rem',
      fontWeight: '600',
      cursor: 'pointer',
      boxShadow: '0 4px 20px rgba(59, 130, 246, 0.4)',
      display: 'inline-flex',
      alignItems: 'center',
      gap: '0.5rem'
    },
    transparent: {
      display: 'flex',
      alignItems: 'center',
      gap: '1rem',
      background: 'transparent',
      border: 'none',
      cursor: 'pointer',
      padding: '0.5rem 0.75rem',
      borderRadius: '0.75rem'
    },
    text: {
      background: 'none',
      border: 'none',
      cursor: 'pointer',
      color: '#3b82f6',
      fontWeight: '500',
      fontSize: '0.875rem',
      padding: '0.5rem'
    },
    textDanger: {
      background: 'none',
      border: 'none',
      cursor: 'pointer',
      color: '#ef4444',
      fontWeight: '500',
      fontSize: '0.875rem',
      padding: '0.5rem'
    },
    danger: {
      background: '#fef2f2',
      color: '#dc2626',
      border: '1px solid #fecaca',
      borderRadius: '0.5rem',
      padding: '0.625rem 1.25rem',
      fontSize: '0.875rem',
      fontWeight: '500',
      cursor: 'pointer'
    },
    icon: {
      padding: '0.5rem',
      background: 'transparent',
      border: 'none',
      borderRadius: '0.375rem',
      cursor: 'pointer',
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center'
    },
    iconSmall: {
      padding: '0.25rem',
      background: 'transparent',
      border: 'none',
      borderRadius: '0.25rem',
      cursor: 'pointer',
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center'
    },
    iconSuccess: {
      padding: '0.25rem',
      color: '#16a34a',
      background: 'transparent',
      border: 'none',
      borderRadius: '0.25rem',
      cursor: 'pointer',
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center'
    },
    iconDanger: {
      padding: '0.25rem',
      color: '#dc2626',
      background: 'transparent',
      border: 'none',
      borderRadius: '0.25rem',
      cursor: 'pointer',
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center'
    },
    hero: {
      display: 'inline-flex',
      alignItems: 'center',
      gap: '0.75rem',
      padding: '1rem 2rem',
      fontSize: '1rem',
      fontWeight: '600',
      color: 'white',
      background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
      border: 'none',
      borderRadius: '1.25rem',
      cursor: 'pointer',
      boxShadow: '0 8px 32px rgba(59, 130, 246, 0.4)',
      letterSpacing: '-0.2px'
    }
  },

  // Card styles
  card: {
    elevated: {
      backgroundColor: 'white',
      borderRadius: '0.75rem',
      padding: '1.5rem',
      boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1)'
    }
  },

  // Display styles
  display: {
    block: { display: 'block' },
    flex: { display: 'flex' },
    flexAlignCenter: { display: 'flex', alignItems: 'center' }
  },

  // Background styles
  background: {
    white: { backgroundColor: '#ffffff' },
    gray50: { backgroundColor: '#f9fafb' },
    gray100: { backgroundColor: '#f3f4f6' }
  },

  // Layout styles
  layout: {
    container: { padding: '1rem', maxWidth: '100%', marginLeft: 'auto', marginRight: 'auto' },
    maxWidth: { maxWidth: '1200px' },
    flex: { display: 'flex' },
    flexAlignCenter: { display: 'flex', alignItems: 'center' },
    flexCenterAll: { display: 'flex', alignItems: 'center', justifyContent: 'center' },
    flexBetween: { display: 'flex', alignItems: 'center', justifyContent: 'space-between' },
    flexColumn: { display: 'flex', flexDirection: 'column' },
    grid: { display: 'grid' },
    hidden: { display: 'none' },
    relative: { position: 'relative' },
    absolute: { position: 'absolute' },
    fixed: { position: 'fixed' },
    headerButton: {
      display: 'flex',
      alignItems: 'center',
      gap: '0.5rem',
      padding: '0.75rem 1.25rem',
      fontSize: '0.875rem',
      fontWeight: '600',
      color: 'white',
      background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
      border: '1px solid rgba(99, 102, 241, 0.2)',
      borderRadius: '1rem',
      cursor: 'pointer',
      boxShadow: '0 4px 12px rgba(99, 102, 241, 0.25)',
      letterSpacing: '-0.01em'
    },
    headerButtonHover: {
      boxShadow: '0 6px 20px rgba(99, 102, 241, 0.35)',
      filter: 'brightness(1.05)'
    },
    headerButtonSecondary: {
      display: 'flex',
      alignItems: 'center',
      gap: '0.5rem',
      padding: '0.75rem 1.25rem',
      fontSize: '0.875rem',
      fontWeight: '500',
      color: '#6b7280',
      background: 'rgba(255, 255, 255, 0.8)',
      border: '1px solid rgba(209, 213, 219, 0.8)',
      borderRadius: '1rem',
      cursor: 'pointer',
      boxShadow: '0 2px 10px rgba(0, 0, 0, 0.05)',
      backdropFilter: 'blur(10px)'
    },
    headerButtonSecondaryHover: {
      boxShadow: '0 4px 15px rgba(0, 0, 0, 0.1)',
      background: 'rgba(255, 255, 255, 0.95)',
      borderColor: 'rgba(156, 163, 175, 0.5)'
    },
    headerButtonActive: {
      background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
      color: 'white',
      boxShadow: '0 4px 12px rgba(99, 102, 241, 0.25)'
    }
  },

  // Spacing styles
  spacing: {
    paddingNone: { padding: '0' },
    paddingXs: { padding: '0.25rem' },
    paddingSm: { padding: '0.5rem' },
    paddingMd: { padding: '0.75rem' },
    paddingLg: { padding: '1rem' },
    paddingXl: { padding: '1.5rem' },
    padding2xl: { padding: '2rem' },
    px2: { paddingLeft: '0.5rem', paddingRight: '0.5rem' },
    px3: { paddingLeft: '0.75rem', paddingRight: '0.75rem' },
    px4: { paddingLeft: '1rem', paddingRight: '1rem' },
    px6: { paddingLeft: '1.5rem', paddingRight: '1.5rem' },
    py2: { paddingTop: '0.5rem', paddingBottom: '0.5rem' },
    py3: { paddingTop: '0.75rem', paddingBottom: '0.75rem' },
    py4: { paddingTop: '1rem', paddingBottom: '1rem' },
    m0: { margin: '0' },
    m1: { margin: '0.25rem' },
    m2: { margin: '0.5rem' },
    m3: { margin: '0.75rem' },
    marginHorizontalSm: { marginLeft: '0.5rem', marginRight: '0.5rem' },
    marginHorizontalMd: { marginLeft: '0.75rem', marginRight: '0.75rem' },
    marginHorizontalLg: { marginLeft: '1rem', marginRight: '1rem' },
    marginHorizontalXl: { marginLeft: '1.5rem', marginRight: '1.5rem' },
    marginHorizontal2xl: { marginLeft: '2rem', marginRight: '2rem' },
    marginHorizontalAuto: { marginLeft: 'auto', marginRight: 'auto' },
    marginVerticalSm: { marginTop: '0.5rem', marginBottom: '0.5rem' },
    marginVerticalMd: { marginTop: '0.75rem', marginBottom: '0.75rem' },
    marginVerticalLg: { marginTop: '1rem', marginBottom: '1rem' },
    marginTopXs: { marginTop: '0.25rem' },
    marginTopSm: { marginTop: '0.5rem' },
    marginTopMd: { marginTop: '0.75rem' },
    marginTopLg: { marginTop: '1rem' },
    marginBottomXs: { marginBottom: '0.25rem' },
    marginBottomSm: { marginBottom: '0.5rem' },
    marginBottomMd: { marginBottom: '0.75rem' },
    marginBottomLg: { marginBottom: '1rem' },
    marginBottomXl: { marginBottom: '1.5rem' },
    marginLeftSm: { marginLeft: '0.5rem' },
    marginLeftMd: { marginLeft: '0.75rem' },
    marginLeftLg: { marginLeft: '1rem' },
    marginLeftXl: { marginLeft: '1.5rem' },
    marginRightSm: { marginRight: '0.5rem' },
    marginRightMd: { marginRight: '0.75rem' },
    marginRightLg: { marginRight: '1rem' },
    gap1: { gap: '0.25rem' },
    gap2: { gap: '0.5rem' },
    gap3: { gap: '0.75rem' },
    gap4: { gap: '1rem' }
  },

  // Shadow styles
  shadows: {
    sm: { boxShadow: '0 1px 2px 0 rgba(0, 0, 0, 0.05)' },
    md: { boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)' },
    lg: { boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1)' },
    xl: { boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1)' }
  },

  // Typography styles
  typography: {
    h1: { fontSize: '2.25rem', fontWeight: 'bold', lineHeight: 1.2 },
    h2: { fontSize: '1.875rem', fontWeight: 'bold', lineHeight: 1.3 },
    h3: { fontSize: '1.5rem', fontWeight: 'bold', lineHeight: 1.4 },
    h4: { fontSize: '1.25rem', fontWeight: 600, lineHeight: 1.4 },
    h5: { fontSize: '1.125rem', fontWeight: 600, lineHeight: 1.5 },
    body: { fontSize: '1rem', lineHeight: 1.5 },
    bodySmall: { fontSize: '0.875rem', lineHeight: 1.4 },
    caption: { fontSize: '0.75rem', lineHeight: 1.3 },
    textXs: { fontSize: '0.75rem' },
    textSm: { fontSize: '0.875rem' },
    textBase: { fontSize: '1rem' },
    textLg: { fontSize: '1.125rem' },
    textXl: { fontSize: '1.25rem' },
    text2xl: { fontSize: '1.5rem' },
    text3xl: { fontSize: '1.875rem' },
    fontNormal: { fontWeight: 'normal' },
    fontMedium: { fontWeight: 500 },
    fontSemibold: { fontWeight: 600 },
    fontBold: { fontWeight: 'bold' },
    textPrimary: { color: '#1f2937' },
    textSecondary: { color: '#6b7280' },
    textTertiary: { color: '#9ca3af' },
    textWhite: { color: '#ffffff' },
    textCenter: { textAlign: 'center' },
    textLeft: { textAlign: 'left' },
    textRight: { textAlign: 'right' }
  },

  // Color styles
  colors: {
    primary: { color: '#3b82f6' },
    primaryLight: { color: '#60a5fa' },
    primaryDark: { color: '#1d4ed8' },
    secondary: { color: '#6b7280' },
    success: { color: '#10b981' },
    warning: { color: '#f59e0b' },
    error: { color: '#ef4444' },
    background: { backgroundColor: '#ffffff' },
    surface: { backgroundColor: '#f8fafc' },
    surfaceLight: { backgroundColor: '#f1f5f9' },
    border: { borderColor: '#e2e8f0' },
    borderLight: { borderColor: '#f1f5f9' },
    text: { color: '#1f2937' },
    textLight: { color: '#6b7280' },
    textDark: { color: '#111827' }
  },

  // Utility styles
  utility: {
    roundedSm: { borderRadius: '0.125rem' },
    roundedMd: { borderRadius: '0.375rem' },
    roundedLg: { borderRadius: '0.5rem' },
    roundedFull: { borderRadius: '9999px' },
    rounded: { borderRadius: '0.25rem' },
    shadow: { boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)' },
    shadowMd: { boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)' },
    shadowLg: { boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)' },
    cursor: { cursor: 'pointer' },
    pointerEventsNone: { pointerEvents: 'none' as const },
    overflowHidden: { overflow: 'hidden' },
    overflowAuto: { overflow: 'auto' },
    border: { border: '1px solid #e5e7eb' },
    borderTop: { borderTop: '1px solid #e5e7eb' },
    glass: { backdropFilter: 'blur(10px)', background: 'rgba(255, 255, 255, 0.8)' },
    zIndex10: { zIndex: 10 },
    zIndex50: { zIndex: 50 },
    opacity90: { opacity: 0.9 }
  },

  // Position styles
  position: {
    relative: { position: 'relative' },
    absolute: { position: 'absolute' },
    fixed: { position: 'fixed' },
    sticky: { position: 'sticky' },
    inset0: { top: '0', right: '0', bottom: '0', left: '0' },
    top0: { top: '0' },
    right0: { right: '0' },
    bottom0: { bottom: '0' },
    left0: { left: '0' }
  },

  // Table styles
  table: {
    container: { width: '100%', overflowX: 'auto' },
    controlsWrapper: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      marginBottom: '1rem',
      gap: '1rem',
      flexWrap: 'wrap' as const
    },
    searchResultsBar: {
      padding: '0.75rem 1rem',
      backgroundColor: '#f8fafc',
      borderRadius: '0.5rem',
      marginBottom: '1rem',
      border: '1px solid #e2e8f0'
    },
    searchResultsText: { fontSize: '0.875rem', color: '#475569', margin: '0' },
    searchResultsHighlight: { fontWeight: '600', color: '#1e40af' },
    spacerRow: { height: '1rem' },
    dragHandle: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      width: '2rem',
      height: '2rem',
      cursor: 'grab',
      opacity: 0,
    },
    dragHandleVisible: { opacity: 1 },
    dragOverlay: {
      padding: '0.5rem 1rem',
      backgroundColor: '#ffffff',
      borderRadius: '0.375rem',
      boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
      zIndex: 9999
    },
    dragOverlayCategory: { fontSize: '0.875rem', fontWeight: '600', color: '#1f2937' },
    dragOverlaySubcategory: { fontSize: '0.875rem', fontWeight: '500', color: '#6b7280' },
    dragOverlayItem: { fontSize: '0.875rem', color: '#374151' },
    mainContainer: { width: '100%' },
    actionMenuWrapper: {
      position: 'relative' as const,
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      width: '100%',
      height: '100%'
    },
    actionCellWrapper: {
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      width: '100%',
      height: '100%',
      minHeight: '32px'
    },
    cellActions: {
      padding: '0.375rem 1rem !important' as any,
      fontSize: '0.875rem',
      color: '#475569',
      verticalAlign: 'middle' as const,
      position: 'relative' as const,
      borderBottom: '1px solid rgba(226, 232, 240, 0.3)',
      lineHeight: '1.2',
      height: '32px',
      textAlign: 'center' as const
    },
    actionButton: {
      background: 'rgba(255, 255, 255, 0.8)',
      backdropFilter: 'blur(10px)',
      border: '1px solid rgba(229, 231, 235, 0.5)',
      padding: '0.375rem',
      cursor: 'pointer',
      borderRadius: '0.5rem',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      boxShadow: '0 1px 3px rgba(0, 0, 0, 0.05)'
    },
    actionButtonHover: {
      background: 'rgba(99, 102, 241, 0.1)',
      borderColor: 'rgba(99, 102, 241, 0.2)',
      boxShadow: '0 4px 8px rgba(99, 102, 241, 0.15)'
    },
    actionIcon: {
      color: '#6b7280',
    },
    actionIconHover: {
      color: '#3b82f6'
    },
    menuContainer: {
      position: 'fixed' as const,
      backgroundColor: 'rgba(255, 255, 255, 0.98)',
      backdropFilter: 'blur(20px)',
      borderRadius: '0.75rem',
      boxShadow: '0 20px 40px rgba(0, 0, 0, 0.12), 0 0 1px rgba(0, 0, 0, 0.05)',
      border: '1px solid rgba(229, 231, 235, 0.5)',
      padding: '0.375rem',
      zIndex: 10000,
      minWidth: '200px'
    },
    menuButton: {
      display: 'flex',
      alignItems: 'center',
      gap: '0.75rem',
      width: '100%',
      padding: '0.625rem 0.875rem',
      background: 'transparent',
      border: 'none',
      borderRadius: '0.5rem',
      fontSize: '0.875rem',
      color: '#374151',
      cursor: 'pointer',
      fontWeight: '500'
    },
    menuButtonHover: {
      backgroundColor: 'rgba(99, 102, 241, 0.08)',
      color: '#3b82f6',
    },
    menuButtonIcon: {
    },
    colorPickerContainer: {
      padding: '0.75rem',
      borderTop: '1px solid rgba(229, 231, 235, 0.5)',
      marginTop: '0.375rem'
    },
    colorPickerGrid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(5, 1fr)',
      gap: '0.625rem'
    },
    colorButton: {
      width: '2.25rem',
      height: '2.25rem',
      borderRadius: '0.5rem',
      border: '2px solid transparent',
      cursor: 'pointer',
      boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)'
    },
    colorButtonHover: {
      borderColor: 'rgba(255, 255, 255, 0.8)',
      boxShadow: '0 4px 12px rgba(0, 0, 0, 0.2)'
    },
    expanderCell: {
      padding: '0.375rem 1px 0.375rem 1rem',
      fontSize: '0.875rem',
      verticalAlign: 'middle' as const,
      borderBottom: '1px solid rgba(226, 232, 240, 0.3)'
    },
    contentCell: {
      padding: '0.375rem 1rem 0.375rem 1px',
      fontSize: '0.875rem',
      color: '#475569',
      verticalAlign: 'middle' as const,
      borderBottom: '1px solid rgba(226, 232, 240, 0.3)',
      lineHeight: '1.2',
      width: '400px',
      maxWidth: '400px'
    },
    contentHeaderCell: {
      width: '400px',
      maxWidth: '400px'
    },
    levelOneBackground: {
      background: 'rgba(248, 250, 252, 0.6)'
    },
    levelTwoBackground: {
      background: 'rgba(248, 250, 252, 0.4)'
    },
    levelTwoContentPadding: {
      paddingLeft: 'calc(1px + 2rem)'
    },
    levelThreeContentPadding: {
      paddingLeft: 'calc(1px + 3rem)'
    },
    expandIconButton: {
      background: 'transparent',
      border: 'none',
      padding: '0.25rem',
      cursor: 'pointer',
      borderRadius: '0.25rem'
    },
    expandIcon: { color: '#6b7280' },
    categoryCard: {
      display: 'flex',
      alignItems: 'center',
      padding: '0.1125rem 0.45rem',
      borderRadius: '0.5rem 0.5rem 0 0',
      marginTop: '0.25rem'
    },
    categoryCardContent: {
      display: 'flex',
      alignItems: 'center',
      gap: '0.25rem'
    },
    categoryIcon: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      width: '1.25rem',
      height: '1.25rem',
      borderRadius: '0.25rem',
      backgroundColor: '#eff6ff'
    },
    categoryIconSvg: { color: '#3b82f6', width: '0.875rem', height: '0.875rem' },
    categoryInfo: { flex: '1' },
    categoryTitle: {
      fontSize: '0.875rem',
      fontWeight: '600',
      color: '#1f2937',
      margin: '0',
      lineHeight: '1.1'
    },
    subcategoryRow: {
      paddingLeft: '1rem',
      paddingTop: '0.075rem',
      paddingBottom: '0.075rem'
    },
    subcategoryText: {
      display: 'flex',
      alignItems: 'center',
      gap: '0.25rem',
      fontSize: '0.875rem',
      color: '#4b5563',
      lineHeight: '1.1'
    },
    subcategoryIcon: { color: '#9ca3af', width: '0.75rem', height: '0.75rem' },
    itemRow: {
      paddingLeft: '2.5rem',
      paddingTop: '0.05rem',
      paddingBottom: '0.05rem',
      paddingRight: '0.5rem'
    },
    itemRowWithSubcategory: {
      paddingLeft: '1rem',
      paddingTop: '0.05rem',
      paddingBottom: '0.05rem',
      paddingRight: '0.5rem'
    },
    itemRowWithoutSubcategory: {
      paddingLeft: '0',
      paddingTop: '0.05rem',
      paddingBottom: '0.05rem',
      paddingRight: '0.5rem'
    },
    itemContent: {
      display: 'flex',
      alignItems: 'center',
      gap: '0.25rem',
      width: '100%'
    },
    itemInfo: {
      fontSize: '0.875rem',
      color: '#374151',
      lineHeight: '1.1',
      textDecoration: 'underline',
      textDecorationColor: 'rgba(55, 65, 81, 0.3)',
      textUnderlineOffset: '2px'
    },
    itemLink: {
      color: '#3b82f6',
      textDecoration: 'underline',
      textDecorationColor: 'rgba(59, 130, 246, 0.4)',
      textUnderlineOffset: '2px',
    },
    tagPillContainer: {
      display: 'flex',
      gap: '0.375rem',
      flexWrap: 'wrap' as const,
      alignItems: 'center',
      justifyContent: 'flex-start',
      marginLeft: '0'
    },
    tagPill: {
      padding: '0.1875rem 0.625rem',
      backgroundColor: '#e0f2fe',
      color: '#075985',
      borderRadius: '9999px',
      fontSize: '0.8125rem',
      fontWeight: '500',
      whiteSpace: 'nowrap' as const
    },
    tagCounter: {
      padding: '0.1875rem 0.625rem',
      backgroundColor: '#f3f4f6',
      color: '#6b7280',
      borderRadius: '9999px',
      fontSize: '0.8125rem',
      fontWeight: '500'
    },
    notesContainer: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'flex-start',
      gap: '0.5rem',
      marginLeft: '0'
    },
    notesDescription: {
      fontSize: '0.875rem',
      color: '#475569',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap' as const,
      maxWidth: '240px',
      lineHeight: '1.5'
    },
    commentButton: {
      display: 'inline-flex',
      alignItems: 'center',
      gap: '0.25rem',
      padding: '0.125rem 0.375rem',
      backgroundColor: '#f3f4f6',
      borderRadius: '0.25rem',
      border: 'none',
      cursor: 'pointer',
      fontSize: '0.75rem',
      color: '#6b7280'
    },
    progressContainer: {
      display: 'flex',
      alignItems: 'center',
      gap: '0.5rem',
      marginLeft: '0'
    },
    progressBarWrapper: {
      flex: '1',
      maxWidth: '100px'
    },
    progressBarBackground: {
      height: '6px',
      backgroundColor: '#e5e7eb',
      borderRadius: '9999px',
      overflow: 'hidden'
    },
    progressText: {
      fontSize: '0.75rem',
      color: '#6b7280',
      minWidth: '2.5rem'
    },
    statusBadge: {
      display: 'inline-flex',
      alignItems: 'center',
      padding: '0.25rem 0.75rem',
      borderRadius: '9999px',
      fontSize: '0.8125rem',
      fontWeight: '500',
      letterSpacing: '0.025em'
    },
    statusBacklog: {
      backgroundColor: 'rgba(147, 197, 253, 0.3)',
      color: '#1e40af',
      border: '1px solid rgba(59, 130, 246, 0.3)'
    },
    statusInProgress: {
      backgroundColor: 'rgba(96, 165, 250, 0.3)',
      color: '#1e3a8a',
      border: '1px solid rgba(37, 99, 235, 0.3)'
    },
    statusDone: {
      backgroundColor: 'rgba(134, 239, 172, 0.3)',
      color: '#14532d',
      border: '1px solid rgba(34, 197, 94, 0.3)'
    },
    statusBlocked: {
      backgroundColor: 'rgba(251, 191, 36, 0.3)',
      color: '#78350f',
      border: '1px solid rgba(245, 158, 11, 0.3)'
    },
    headerCell: {
      fontWeight: '600',
      fontSize: '0.875rem',
      color: '#374151'
    },
    tagHeaderCell: {
      display: 'inline-block'
    },
    notesHeaderCell: {
      display: 'inline-block'
    },
    linkText: {
      color: '#000000',
      textDecoration: 'none',
      fontWeight: 500,
    },
    linkTextHover: {
      color: '#3b82f6',
      textDecoration: 'underline'
    },
    regularText: {
      color: '#000000',
      fontWeight: 500
    },
    categoryBadge: {
      padding: '0.25rem 0.5rem',
      backgroundColor: '#eff6ff',
      color: '#1e40af',
      borderRadius: '0.25rem',
      fontSize: '0.75rem',
      fontWeight: '500'
    },
    emptyCell: {
      color: '#9ca3af'
    }
  },

  // Notes styles
  notes: {
    header: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      padding: '1rem',
      borderBottom: '1px solid #e2e8f0',
      backgroundColor: '#ffffff'
    },
    title: { fontSize: '1.5rem', fontWeight: '700', color: '#1f2937', margin: '0' },
    newNoteButton: {
      display: 'flex',
      alignItems: 'center',
      gap: '0.5rem',
      padding: '0.5rem 1rem',
      backgroundColor: '#3b82f6',
      color: '#ffffff',
      border: 'none',
      borderRadius: '0.5rem',
      fontSize: '0.875rem',
      fontWeight: '500',
      cursor: 'pointer',
    },
    sidebar: {
      width: '320px',
      borderRight: '1px solid #e2e8f0',
      backgroundColor: '#f8fafc',
      display: 'flex',
      flexDirection: 'column'
    },
    searchBox: { padding: '1rem' },
    searchInput: {
      width: '100%',
      padding: '0.75rem 0.75rem 0.75rem 2.5rem',
      fontSize: '0.875rem',
      border: '1px solid #d1d5db',
      borderRadius: '0.5rem',
      backgroundColor: '#ffffff',
    },
    notesList: { flex: '1', overflowY: 'auto' },
    emptyState: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '3rem 1rem',
      textAlign: 'center'
    },
    emptyStateIcon: { color: '#9ca3af', marginBottom: '1rem' },
    emptyStateText: { color: '#6b7280', fontSize: '0.875rem', margin: '0' },
    noteItem: {
      padding: '0.75rem 1rem',
      cursor: 'pointer',
      borderBottom: '1px solid #e5e7eb',
    },
    noteItemSelected: { backgroundColor: '#eff6ff', borderLeft: '3px solid #3b82f6' },
    noteItemTitle: {
      fontSize: '0.875rem',
      fontWeight: '500',
      color: '#1f2937',
      margin: '0',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap'
    },
    editor: {
      flex: '1',
      display: 'flex',
      flexDirection: 'column',
      backgroundColor: '#ffffff'
    },
    editorHeader: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      padding: '1rem',
      borderBottom: '1px solid #e2e8f0'
    },
    editorTitle: {
      flex: '1',
      padding: '0 0 1rem 0',  // No top/side padding, nice bottom spacing
      fontSize: '2.25rem',  // Large, bold title like Substack articles
      fontWeight: '700',
      border: 'none',
      backgroundColor: 'transparent',
      outline: 'none',
      color: '#0f172a',  // Rich, dark color for titles
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',  // Clean sans-serif for titles
      letterSpacing: '-0.025em',  // Tighter letter spacing for titles
      lineHeight: '1.3',
      maxWidth: '700px',  // Match content width
      width: '100%',
      textRendering: 'optimizeLegibility',
      WebkitFontSmoothing: 'antialiased'
    },
    editorContent: { flex: '1', position: 'relative' },
    editorContentArea: {
      width: '100%',
      minHeight: '400px',
      padding: '3rem 4rem',  // Match the modern editor padding
      fontSize: '1.25rem',  // Larger, readable text like Substack
      lineHeight: '1.9',  // Generous line spacing
      letterSpacing: '0.01em',  // Better letter spacing
      wordSpacing: '0.02em',  // Better word spacing
      border: 'none',
      resize: 'none',
      outline: 'none',
      backgroundColor: 'transparent',
      color: '#1a1a1a',  // Rich, dark text
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',  // Beautiful serif
      maxWidth: '700px',  // Optimal reading width
      margin: '0 auto',  // Center the content
      textRendering: 'optimizeLegibility',
      WebkitFontSmoothing: 'antialiased',
      MozOsxFontSmoothing: 'grayscale'
    },
    editorPlaceholder: {
      color: '#9ca3af',
      fontStyle: 'italic',
      pointerEvents: 'none'
    },
    savingIndicator: {
      fontSize: '0.75rem',
      color: '#10b981',
      fontStyle: 'italic'
    },
    // Centralized styles for NotesView component
    headerFlexCenter: {
      display: 'flex',
      alignItems: 'center',
      gap: '0.75rem'
    },
    headerFlexCenterGap2: {
      display: 'flex',
      alignItems: 'center',
      gap: '0.5rem'
    },
    iconPrimary: {
      color: '#2563eb'
    },
    iconSecondary: {
      color: 'var(--color-gray-600)'
    },
    mainContainer: {
      display: 'flex',
      flex: 1,
      overflow: 'hidden'
    },
    searchIconPosition: {
      position: 'absolute' as const,
      left: '0.75rem',
      top: '50%',
      transform: 'translateY(-50%)',
      color: 'var(--color-text-tertiary)'
    },
    buttonWithMargin: {
      marginTop: '1rem'
    },
    editorPlaceholderAbsolute: {
      position: 'absolute' as const,
      pointerEvents: 'none' as const
    },
    emptyStateContainer: {
      flex: 1,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center'
    },
    emptyStateTitle: {
      fontSize: '12px',
      fontWeight: 500,
      color: '#757575',
      marginBottom: '8px'
    }
  },

  // Extended spacing
  extended: {
    p05: { padding: '0.125rem' },
    pt4: { paddingTop: '1rem' },
    pl8: { paddingLeft: '2rem' },
    px5: { paddingLeft: '1.25rem', paddingRight: '1.25rem' },
    py25: { paddingTop: '0.625rem', paddingBottom: '0.625rem' },
    gridCols2: { gridTemplateColumns: 'repeat(2, 1fr)' },
    gradientBluePurple: { background: '#3b82f6' },
    gradientGreen: { background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)' },
    gradientBlue: { background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)' },
    gradientYellow: { background: 'linear-gradient(135deg, #fbbf24 0%, #f59e0b 100%)' }
  },

  // Component styles
  components: {
    settingsMenu: {
      dropdown: {
        position: 'absolute' as const,
        right: 0,
        marginTop: '0.5rem',
        width: '16rem',
        backgroundColor: '#ffffff',
        borderRadius: '0.5rem',
        border: '1px solid var(--color-gray-200)',
        overflow: 'hidden',
        boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
        zIndex: 10001
      }
    },
    categoryModal: {
      overlay: {
        position: 'fixed' as const,
        inset: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        backdropFilter: 'blur(4px)',
        display: 'flex',
        alignItems: 'flex-start',
        justifyContent: 'center',
        paddingTop: '100px',
        paddingBottom: '40px',
        paddingLeft: '1rem',
        paddingRight: '1rem',
        overflowY: 'auto',
        zIndex: 1000,
      },
      container: {
        backgroundColor: '#ffffff',
        borderRadius: '1.5rem',
        maxWidth: '48rem',
        width: '100%',
        maxHeight: 'calc(100vh - 140px)',
        overflow: 'hidden',
        boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
        display: 'flex',
        flexDirection: 'column',
      },
      content: {
        padding: '2rem',
        overflowY: 'auto',
        flex: 1
      },
      header: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'flex-start',
        marginBottom: '2rem',
        padding: '2rem 2rem 0 2rem'
      },
      title: {
        fontSize: '1.5rem',
        fontWeight: '700',
        color: '#111827',
        letterSpacing: '-0.025em',
        margin: 0
      },
      subtitle: {
        fontSize: '0.875rem',
        color: '#6b7280',
        marginTop: '0.25rem'
      },
      closeButton: {
        padding: '0.5rem',
        borderRadius: '0.5rem',
        background: 'transparent',
        border: 'none',
        cursor: 'pointer',
          display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      },
      categoryCard: {
        border: '1px solid #e5e7eb',
        borderRadius: '1rem',
        overflow: 'hidden',
        marginBottom: '1rem',
        background: '#ffffff',
        boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)'
      },
      categoryHeader: {
        padding: '0.6rem 1.25rem',
        background: 'linear-gradient(to right, #f9fafb, #f3f4f6)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        borderBottom: '1px solid #e5e7eb',
      },
      categoryHeaderExpanded: {
        background: 'linear-gradient(to right, #eff6ff, #dbeafe)'
      },
      categoryName: {
        fontSize: '1rem',
        fontWeight: '600',
        color: '#111827',
        display: 'flex',
        alignItems: 'center',
        gap: '0.75rem',
        flex: 1
      },
      categoryActions: {
        display: 'flex',
        alignItems: 'center',
        gap: '0.5rem'
      },
      expandButton: {
        padding: '0.25rem',
        borderRadius: '0.375rem',
        background: 'transparent',
        border: 'none',
        cursor: 'pointer',
        color: '#6b7280',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        },
      actionButton: {
        padding: '0.375rem',
        borderRadius: '0.375rem',
        background: 'transparent',
        border: 'none',
        cursor: 'pointer',
        color: '#6b7280',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        },
      actionButtonDanger: {
        color: '#ef4444'
      },
      subcategoryList: {
        padding: '0.75rem',
        background: '#fafafa'
      },
      subcategoryItem: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: '0.75rem 1rem',
        marginBottom: '0.5rem',
        background: '#ffffff',
        borderRadius: '0.5rem',
        border: '1px solid #e5e7eb',
      },
      subcategoryText: {
        fontSize: '0.875rem',
        color: '#374151',
        fontWeight: '500'
      },
      emptyState: {
        padding: '2rem',
        textAlign: 'center' as const,
        color: '#9ca3af',
        fontSize: '0.875rem',
        fontStyle: 'italic'
      },
      addButton: {
        width: '100%',
        padding: '0.75rem',
        marginTop: '1.5rem',
        borderRadius: '0.75rem',
        border: '2px dashed #d1d5db',
        background: 'transparent',
        color: '#6b7280',
        fontSize: '0.875rem',
        fontWeight: '500',
        cursor: 'pointer',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '0.5rem',
      },
      addButtonPrimary: {
        background: '#3b82f6',
        color: '#ffffff',
        border: 'none',
        borderRadius: '0.75rem',
        padding: '0.75rem 1.5rem',
        fontSize: '0.875rem',
        fontWeight: '600',
        cursor: 'pointer',
          boxShadow: '0 4px 6px -1px rgba(99, 102, 241, 0.3)'
      },
      inputField: {
        padding: '0.5rem 0.75rem',
        fontSize: '0.875rem',
        border: '1px solid #d1d5db',
        borderRadius: '0.375rem',
        flex: 1,
        outline: 'none'
      },
      inputWrapper: {
        display: 'flex',
        alignItems: 'center',
        gap: '0.5rem',
        flex: 1
      }
    },
    localTimeDisplay: {
      container: {
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        padding: '8px 16px',
        borderRadius: '12px',
        background: 'rgba(59, 130, 246, 0.1)',
        border: '1px solid rgba(59, 130, 246, 0.2)'
      },
      icon: {
        color: '#3b82f6'
      },
      text: {
        fontSize: '13px',
        fontWeight: '500',
        color: '#2563eb',
        fontFamily: 'monospace'
      }
    },
    localDateDisplay: {
      container: {
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        padding: '8px 16px',
        borderRadius: '12px',
        background: 'rgba(59, 130, 246, 0.1)',
        border: '1px solid rgba(59, 130, 246, 0.2)'
      },
      icon: {
        color: '#3b82f6'
      },
      text: {
        fontSize: '13px',
        fontWeight: '500',
        color: '#2563eb'
      }
    },
    searchBar: {
      container: {
        position: 'relative',
        display: 'flex',
        alignItems: 'center',
        gap: '0.75rem'
      },
      input: {
        padding: '0.75rem 1rem 0.75rem 2.75rem',
        fontSize: '0.875rem',
        border: '1px solid #d1d5db',
        borderRadius: '0.5rem',
        width: '900px',
        backgroundColor: '#ffffff'
      },
      inputActive: {
        borderColor: '#3b82f6',
        boxShadow: '0 0 0 3px rgba(59, 130, 246, 0.1)'
      },
      inputFocus: {
        borderColor: '#3b82f6',
        outline: 'none',
        boxShadow: '0 0 0 3px rgba(59, 130, 246, 0.1)'
      },
      icon: {
        position: 'absolute',
        left: '0.75rem',
        color: '#9ca3af'
      },
      results: {
        fontSize: '0.75rem',
        color: '#6b7280',
        marginLeft: '0.5rem'
      },
      resultsText: {
        fontSize: '0.875rem',
        color: '#475569'
      },
      clearButton: {
        position: 'absolute',
        right: '0.5rem',
        padding: '0.25rem',
        backgroundColor: 'transparent',
        border: 'none',
        borderRadius: '0.25rem',
        cursor: 'pointer',
        color: '#9ca3af',
        }
    },
    viewToggle: {
      container: {
        display: 'flex',
        alignItems: 'center',
        gap: '0.75rem'
      },
      buttonGroup: {
        display: 'flex',
        borderRadius: '0.5rem',
        border: '1px solid #d1d5db',
        backgroundColor: '#ffffff',
        overflow: 'hidden'
      },
      button: {
        display: 'flex',
        alignItems: 'center',
        gap: '0.5rem',
        padding: '0.5rem 0.75rem',
        fontSize: '0.875rem',
        fontWeight: '500',
        border: 'none',
        backgroundColor: 'transparent',
        cursor: 'pointer',
          color: '#6b7280'
      },
      buttonActive: {
        backgroundColor: '#3b82f6',
        color: '#ffffff'
      },
      buttonHover: {
        backgroundColor: '#f3f4f6',
        color: '#1f2937'
      },
      expandButton: {
        padding: '0.5rem',
        fontSize: '0.75rem',
        color: '#6b7280',
        backgroundColor: 'transparent',
        border: '1px solid #d1d5db',
        borderRadius: '0.375rem',
        cursor: 'pointer',
      },
      expandButtonHover: {
        backgroundColor: '#f3f4f6',
        borderColor: '#9ca3af'
      },
      collapseButton: {
        padding: '0.5rem',
        fontSize: '0.75rem',
        color: '#6b7280',
        backgroundColor: 'transparent',
        border: '1px solid #d1d5db',
        borderRadius: '0.375rem',
        cursor: 'pointer',
      },
      collapseButtonHover: {
        backgroundColor: '#f3f4f6',
        borderColor: '#9ca3af'
      },
      iconButton: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: '2.5rem',
        height: '2.5rem',
        backgroundColor: 'transparent',
        border: '1px solid #d1d5db',
        borderRadius: '0.375rem',
        cursor: 'pointer',
      },
      iconButtonHover: {
        backgroundColor: '#3b82f6',
        borderColor: '#3b82f6',
      }
    },
    categoryCard: {
      container: {
        marginBottom: '1rem',
        borderRadius: '1rem',
        padding: '1.5rem',
        cursor: 'pointer',
        border: '1px solid rgba(226, 232, 240, 0.5)'
      },
      containerHover: { },
      header: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        cursor: 'pointer'
      },
      leftSection: {
        display: 'flex',
        alignItems: 'center',
        gap: '1rem',
        flex: 1
      },
      expandButton: {
        padding: '0.5rem',
        backgroundColor: 'transparent',
        border: 'none',
        borderRadius: '0.5rem',
        cursor: 'pointer',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        },
      avatar: {
        width: '3rem',
        height: '3rem',
        borderRadius: '0.75rem',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: '1.25rem',
        fontWeight: '700',
        color: '#ffffff',
        textShadow: '0 1px 2px rgba(0, 0, 0, 0.1)',
        cursor: 'move'
      },
      contentSection: {
        flex: 1,
        minWidth: 0
      },
      titleRow: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        marginBottom: '0.75rem'
      },
      title: {
        fontSize: '1.375rem',
        fontWeight: '700',
        margin: 0,
        letterSpacing: '-0.025em'
      },
      completionBadge: {
        fontSize: '0.75rem',
        fontWeight: '600',
        padding: '0.25rem 0.625rem',
        borderRadius: '9999px',
        lineHeight: 1
      },
      statsRow: {
        display: 'flex',
        alignItems: 'center',
        gap: '1.5rem',
        marginBottom: '1rem'
      },
      statItem: {
        display: 'flex',
        alignItems: 'center',
        gap: '0.375rem'
      },
      statText: {
        fontSize: '0.875rem',
        fontWeight: '500'
      },
      progressBar: {
        width: '100%',
        height: '0.5rem',
        borderRadius: '9999px',
        overflow: 'hidden'
      },
      progressFill: {
        height: '100%',
        borderRadius: '9999px',
      },
      actionsSection: {
        display: 'flex',
        alignItems: 'center',
        gap: '0.75rem'
      },
      addButton: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: '2.5rem',
        height: '2.5rem',
        border: 'none',
        borderRadius: '0.75rem',
        cursor: 'pointer',
          color: '#ffffff'
      },
      menuButton: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: '2.5rem',
        height: '2.5rem',
        backgroundColor: 'transparent',
        border: 'none',
        borderRadius: '0.5rem',
        cursor: 'pointer',
        },
      dropdown: {
        position: 'absolute',
        top: '100%',
        right: 0,
        marginTop: '0.5rem',
        borderRadius: '0.5rem',
        padding: '0.5rem',
        zIndex: 50,
        minWidth: '160px'
      },
      dropdownItem: {
        display: 'flex',
        alignItems: 'center',
        gap: '0.5rem',
        width: '100%',
        padding: '0.5rem 0.75rem',
        fontSize: '0.875rem',
        fontWeight: '500',
        backgroundColor: 'transparent',
        border: 'none',
        borderRadius: '0.375rem',
        cursor: 'pointer',
        textAlign: 'left'
      }
    },
    modernTable: {
      container: {
        background: 'linear-gradient(145deg, #ffffff 0%, #fbfcfd 100%)',
        borderRadius: '1.25rem',
        border: '1px solid rgba(226, 232, 240, 0.8)',
        overflow: 'hidden',
        boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
        backdropFilter: 'blur(10px)',
        position: 'relative' as const
      },
      table: {
        width: '100%',
        borderCollapse: 'separate' as const,
        borderSpacing: '0',
        position: 'relative' as const
      },
      header: {
        background: 'linear-gradient(to bottom, rgba(248, 250, 252, 0.95), rgba(241, 245, 249, 0.95))',
        borderBottom: '2px solid rgba(99, 102, 241, 0.1)',
        backdropFilter: 'blur(8px)',
        position: 'sticky' as const,
        top: 0,
        zIndex: 10
      },
      headerCell: {
        padding: '0.5rem 1rem',
        fontSize: '0.75rem',
        fontWeight: '600',
        textTransform: 'uppercase' as const,
        letterSpacing: '0.05em',
        color: '#64748b',
        textAlign: 'left' as const,
        borderBottom: '2px solid rgba(99, 102, 241, 0.15)',
        background: 'linear-gradient(to bottom, rgba(248, 250, 252, 0.98), rgba(241, 245, 249, 0.98))',
        whiteSpace: 'nowrap' as const,
        height: '36px',
        lineHeight: '1.2'
      },
      row: {
        borderBottom: '1px solid rgba(226, 232, 240, 0.3)',
          position: 'relative' as const
      },
      rowHover: {
        backgroundColor: 'rgba(99, 102, 241, 0.02)',
        boxShadow: 'inset 3px 0 0 #3b82f6'
      },
      cell: {
        padding: '0.375rem 1rem',
        fontSize: '0.875rem',
        color: '#475569',
        verticalAlign: 'middle' as const,
          position: 'relative' as const,
        borderBottom: '1px solid rgba(226, 232, 240, 0.3)',
        lineHeight: '1.2',
        height: '32px'
      },
      actionButton: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '0.375rem',
        background: 'transparent',
        border: 'none',
        borderRadius: '0.375rem',
        cursor: 'pointer',
        color: '#9ca3af',
          fontSize: '0.875rem',
        fontWeight: '500',
        width: '32px',
        height: '32px',
        margin: '0 auto'
      },
      actionButtonHover: {
        background: 'rgba(99, 102, 241, 0.08)',
        color: '#3b82f6',
          boxShadow: 'none',
        borderColor: 'transparent'
      },
      dragHandleCell: {
        padding: '0.5rem',
        width: '3rem'
      },
      dragHandleHidden: {
        opacity: 0,
          cursor: 'default'
      },
      dragHandleVisible: {
        opacity: 1,
          cursor: 'move'
      },
      dragHandlePlaceholder: {
        width: '2rem',
        height: '2rem'
      },
      dragHandleIcon: {
        color: 'currentColor',
        },
      dragHandleIconHover: {
        color: '#3b82f6'
        }
    },
    inlineEdit: {
      container: {
        display: 'inline-block',
        cursor: 'pointer',
        minWidth: '100px',
        padding: '0.25rem',
        borderRadius: '0.375rem',
        transition: 'background-color 0.2s'
      },
      editContainer: {
        position: 'relative' as const,
        width: '100%'
      },
      tagsContainer: {
        display: 'flex',
        flexWrap: 'wrap' as const,
        gap: '0.25rem',
        minHeight: '2rem',
        alignItems: 'center',
        cursor: 'pointer',
        padding: '0.25rem',
        borderRadius: '0.375rem',
        transition: 'background-color 0.2s'
      },
      tagsEditWrapper: {
        display: 'flex',
        flexWrap: 'wrap' as const,
        gap: '0.25rem',
        padding: '0.5rem',
        border: '2px solid #3b82f6',
        borderRadius: '0.375rem',
        backgroundColor: 'white',
        minHeight: '2.5rem',
        alignItems: 'center'
      },
      tagPillEdit: {
        display: 'inline-flex',
        alignItems: 'center',
        gap: '0.25rem',
        padding: '0.25rem 0.5rem',
        backgroundColor: 'rgba(59, 130, 246, 0.1)',
        color: '#1e40af',
        borderRadius: '9999px',
        fontSize: '0.75rem',
        fontWeight: '500'
      },
      tagRemoveButton: {
        background: 'none',
        border: 'none',
        cursor: 'pointer',
        padding: '0',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: '#6b7280',
        transition: 'color 0.2s'
      },
      tagInput: {
        flex: '1',
        minWidth: '100px',
        padding: '0.25rem',
        border: 'none',
        outline: 'none',
        fontSize: '0.75rem',
        backgroundColor: 'transparent'
      },
      notesContainer: {
        cursor: 'pointer',
        minHeight: '2rem',
        padding: '0.25rem',
        borderRadius: '0.375rem',
        transition: 'background-color 0.2s'
      },
      notesTextarea: {
        width: '100%',
        padding: '0.5rem',
        border: '2px solid #3b82f6',
        borderRadius: '0.375rem',
        fontSize: '0.875rem',
        lineHeight: '1.5',
        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
        resize: 'vertical' as const,
        outline: 'none'
      },
      statusContainer: {
        display: 'inline-block',
        cursor: 'pointer',
        minWidth: '100px',
        padding: '0.25rem',
        borderRadius: '0.375rem',
        transition: 'background-color 0.2s'
      },
      statusSelect: {
        padding: '0.5rem',
        border: '2px solid #3b82f6',
        borderRadius: '0.375rem',
        fontSize: '0.875rem',
        outline: 'none',
        backgroundColor: 'white',
        cursor: 'pointer',
        minWidth: '150px'
      },
      placeholder: {
        color: '#9ca3af',
        fontStyle: 'italic',
        fontSize: '0.875rem'
      },
      suggestions: {
        position: 'absolute' as const,
        top: '100%',
        left: '0',
        right: '0',
        marginTop: '0.25rem',
        backgroundColor: 'white',
        border: '1px solid #e5e7eb',
        borderRadius: '0.375rem',
        boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
        zIndex: 1000,
        maxHeight: '200px',
        overflowY: 'auto' as const
      },
      suggestionItem: {
        display: 'block',
        width: '100%',
        padding: '0.5rem 0.75rem',
        textAlign: 'left' as const,
        fontSize: '0.875rem',
        color: '#374151',
        backgroundColor: 'transparent',
        border: 'none',
        cursor: 'pointer',
        transition: 'background-color 0.2s'
      },
      helpText: {
        fontSize: '0.75rem',
        color: '#6b7280',
        marginTop: '0.25rem',
        fontStyle: 'italic'
      }
    },
    notes: {
      modern: {
        container: {
          display: 'flex',
          flexDirection: 'column',
          height: '100%',
          background: 'linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)',
          borderRadius: '1.25rem',
          overflow: 'hidden'
        },
        editor: {
          flex: '1',
          display: 'flex',
          flexDirection: 'column',
          background: 'linear-gradient(135deg, #ffffff 0%, #fafbfc 100%)',
          minHeight: '60vh',
          fontSize: '1.25rem',  // Larger, more readable text (20px)
          lineHeight: 1.9,  // Generous line spacing like Substack
          letterSpacing: '0.01em',  // Slightly improved letter spacing
          color: '#1a1a1a',  // Darker, richer text color
          caretColor: '#3b82f6',
          padding: '3rem 4rem',  // More generous padding
          maxWidth: '700px',  // Optimal reading width like Substack
          margin: '0 auto',  // Center the content
          fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',  // Beautiful serif font like Substack
          wordSpacing: '0.02em',  // Better word spacing
          textRendering: 'optimizeLegibility',  // Better font rendering
          WebkitFontSmoothing: 'antialiased',  // Smoother fonts
          MozOsxFontSmoothing: 'grayscale'  // Smoother fonts on Mac
        },
        toolbar: {
          background: 'linear-gradient(to right, rgba(99, 102, 241, 0.05), rgba(139, 92, 246, 0.05))',
          backdropFilter: 'blur(12px)',
          borderRadius: '1rem',
          padding: '0.75rem',
          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.08)',
          border: '1px solid rgba(99, 102, 241, 0.1)',
          display: 'flex',
          gap: '0.5rem',
          alignItems: 'center'
        },
        sidebar: {
          width: '320px',
          background: 'linear-gradient(180deg, #ffffff 0%, #f8fafc 100%)',
          borderRight: '1px solid rgba(226, 232, 240, 0.8)',
          display: 'flex',
          flexDirection: 'column',
          boxShadow: '2px 0 8px rgba(0, 0, 0, 0.03)'
        },
        searchBox: {
          padding: '1.25rem',
          borderBottom: '1px solid rgba(226, 232, 240, 0.5)',
          background: 'rgba(255, 255, 255, 0.5)',
          backdropFilter: 'blur(10px)'
        },
        searchInput: {
          width: '100%',
          padding: '0.875rem 0.875rem 0.875rem 2.75rem',
          fontSize: '0.875rem',
          border: '2px solid transparent',
          borderRadius: '0.75rem',
          background: 'linear-gradient(#ffffff, #ffffff) padding-box, linear-gradient(135deg, #3b82f6, #3b82f6) border-box',
            outline: 'none',
          color: '#1e293b'
        },
        noteItem: {
          margin: '0.375rem',
          padding: '1rem',
          borderRadius: '0.75rem',
          cursor: 'pointer',
          background: 'linear-gradient(145deg, #ffffff, #f8fafc)',
          border: '1px solid rgba(226, 232, 240, 0.5)',
          position: 'relative' as const,
          overflow: 'hidden'
        },
        noteItemSelected: {
          background: 'linear-gradient(145deg, #eff6ff, #e0e7ff)',
          borderLeft: '3px solid #3b82f6',
          borderColor: 'rgba(99, 102, 241, 0.4)',
          boxShadow: '0 4px 12px rgba(99, 102, 241, 0.15)'
        },
        actionButton: {
          padding: '0.5rem 1rem',
          fontSize: '0.875rem',
          fontWeight: '500',
          borderRadius: '0.5rem',
          border: 'none',
          cursor: 'pointer',
          background: '#3b82f6',
          color: 'white',
            boxShadow: '0 2px 4px rgba(99, 102, 241, 0.2)',
          display: 'inline-flex',
          alignItems: 'center',
          gap: '0.5rem'
        }
      },
      whiteboard: {
        wrapper: {
          minHeight: 'auto',
          width: '600px',
          margin: '0 auto',
          animation: 'fadeIn 400ms ease-out'
        },
        container: {
          display: 'flex',
          flexDirection: 'column',
          minHeight: 'auto',
          background: 'linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)',
          borderRadius: '1.25rem',
          overflow: 'visible',
          isolation: 'isolate',
          position: 'relative'
        },
        header: {
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '1.25rem 1.5rem',
          background: 'linear-gradient(135deg, #ffffff 0%, #fafbfc 100%)',
          borderBottom: '1px solid rgba(226, 232, 240, 0.8)',
          boxShadow: '0 1px 3px rgba(0, 0, 0, 0.04)',
          borderTopLeftRadius: '1.25rem',
          borderTopRightRadius: '1.25rem',
          position: 'relative',
          zIndex: 10
        },
        headerLeft: {
          display: 'flex',
          alignItems: 'center',
          gap: '0.75rem'
        },
        icon: {
          color: '#3b82f6'
        },
        title: {
          fontSize: '1.5rem',
          fontWeight: '700',
          color: '#1f2937',
          margin: '0',
          letterSpacing: '-0.025em'
        },
        searchContainer: {
          position: 'relative',
          display: 'flex',
          alignItems: 'center',
          flex: '1',
          maxWidth: '400px',
          marginLeft: '2rem',
          marginRight: '2rem'
        },
        searchIcon: {
          position: 'absolute',
          left: '0.75rem',
          color: '#9ca3af',
          pointerEvents: 'none'
        },
        searchInput: {
          width: '100%',
          padding: '0.625rem 2.5rem',
          fontSize: '0.875rem',
          border: '1px solid rgba(209, 213, 219, 0.5)',
          borderRadius: '0.75rem',
          backgroundColor: '#ffffff',
          outline: 'none',
          transition: 'all 0.2s ease'
        },
        clearButton: {
          position: 'absolute',
          right: '0.75rem',
          padding: '0.25rem',
          background: 'transparent',
          border: 'none',
          cursor: 'pointer',
          borderRadius: '0.375rem',
          display: 'flex',
          alignItems: 'center',
          color: '#9ca3af',
          transition: 'all 0.15s ease'
        },
        saveIndicator: {
          fontSize: '0.75rem',
          color: '#10b981',
          fontStyle: 'italic',
          animation: 'fadeIn 0.3s ease'
        },
        editorContainer: {
          flex: '1',
          position: 'relative',
          overflow: 'auto',
          display: 'flex',
          justifyContent: 'center',
          borderBottomLeftRadius: '1.25rem',
          borderBottomRightRadius: '1.25rem',
          minHeight: '90vh'
        },
        placeholder: {
          position: 'absolute',
          top: '3rem',
          left: '50%',
          transform: 'translateX(-50%)',
          color: '#9ca3af',
          fontSize: '1.125rem',
          fontStyle: 'italic',
          pointerEvents: 'none',
          fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif'
        },
        editor: {
          width: '100%',
          maxWidth: '600px',
          minHeight: '90vh',
          padding: '3rem 4rem',
          fontSize: '1.25rem',
          lineHeight: 1.9,
          letterSpacing: '0.01em',
          color: '#1a1a1a',
          fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
          wordSpacing: '0.02em',
          textRendering: 'optimizeLegibility',
          WebkitFontSmoothing: 'antialiased',
          MozOsxFontSmoothing: 'grayscale',
          border: 'none',
          outline: 'none',
          backgroundColor: 'transparent',
          caretColor: '#3b82f6'
        },
        noResults: {
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          color: '#9ca3af',
          fontSize: '0.875rem',
          fontStyle: 'italic',
          textAlign: 'center',
          padding: '2rem',
          backgroundColor: 'rgba(255, 255, 255, 0.9)',
          borderRadius: '0.5rem',
          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)'
        },
        themeSelector: {
          button: {
            padding: '0.5rem',
            borderRadius: '0.5rem',
            backgroundColor: 'transparent',
            border: '1px solid rgba(209, 213, 219, 0.5)',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem',
            color: '#6b7280',
            fontSize: '0.875rem',
            transition: 'all 0.2s ease',
            position: 'relative'
          },
          dropdown: {
            position: 'absolute',
            top: '100%',
            right: '0',
            marginTop: '0.5rem',
            backgroundColor: '#ffffff',
            border: '1px solid rgba(209, 213, 219, 0.5)',
            borderRadius: '0.75rem',
            padding: '0.5rem',
            boxShadow: '0 10px 25px rgba(0, 0, 0, 0.1)',
            minWidth: '180px',
            zIndex: 9999
          },
          dropdownItem: {
            display: 'flex',
            alignItems: 'center',
            gap: '0.75rem',
            padding: '0.625rem 0.75rem',
            borderRadius: '0.5rem',
            cursor: 'pointer',
            transition: 'background-color 0.15s ease',
            fontSize: '0.875rem',
            color: '#374151'
          },
          dropdownItemActive: {
            backgroundColor: '#f3f4f6',
            fontWeight: '500'
          },
          colorDot: {
            width: '20px',
            height: '20px',
            borderRadius: '50%',
            border: '2px solid rgba(0, 0, 0, 0.1)',
            flexShrink: 0
          }
        }
      }
    }
  }
};

// Helper function to conditionally apply styles
export const conditionalStyle = (
  condition: boolean,
  trueStyle: CSSProperties,
  falseStyle?: CSSProperties
): CSSProperties => {
  return condition ? trueStyle : (falseStyle || {});
};

